import type { FrontendStockFormat } from "~/types/global.types";

/** Mizrahi/Open Finance sometimes persist only the last 4 digits of a 6-digit TASE number. */
export function isTruncatedTaseNumber(value: string | undefined | null): boolean {
  return /^\d{4}$/.test(String(value ?? "").trim());
}

const relativeDistance = (left: number, right: number) =>
  Math.abs(left - right) / Math.max(left, right, 1e-9);

/** Banks may store purchase price in ILS or agorot; try both scales. */
const bestPriceDistance = (purchasePrice: number, marketPrice: number) =>
  Math.min(
    relativeDistance(purchasePrice, marketPrice),
    relativeDistance(purchasePrice / 100, marketPrice),
    relativeDistance(purchasePrice * 100, marketPrice)
  );

function pickByPurchasePrice<T>(
  candidates: T[],
  getPrice: (item: T) => number | undefined,
  purchasePrice?: number
): T | undefined {
  if (candidates.length === 1) return candidates[0];
  if (!purchasePrice || purchasePrice <= 0) return undefined;

  const scored = candidates
    .map((item) => {
      const marketPrice = getPrice(item);
      if (!marketPrice || marketPrice <= 0) return null;
      return { item, rel: bestPriceDistance(purchasePrice, marketPrice) };
    })
    .filter((row): row is { item: T; rel: number } => row !== null)
    .sort((left, right) => left.rel - right.rel);

  if (scored.length === 0) return undefined;
  const [best, second] = scored;
  if (best.rel > 0.45) return undefined;
  if (second && second.rel <= 0.45 && second.rel < best.rel * 1.8 && best.rel > 0.08) {
    return undefined;
  }
  return best.item;
}

export function pickUniqueExpandedSecurity<T extends { security_id: string; base_price?: number | null }>(
  suffix: string,
  rows: T[],
  purchasePrice?: number
): T | undefined {
  if (!isTruncatedTaseNumber(suffix)) return undefined;
  const matches = rows.filter(
    (row) => /^\d{6}$/.test(row.security_id) && row.security_id.endsWith(suffix)
  );
  return pickByPurchasePrice(
    matches,
    (row) => (typeof row.base_price === "number" ? row.base_price : undefined),
    purchasePrice
  );
}

/**
 * Mizrahi series month/year ("07/26"). Do not strip allocation ratios
 * ("15/85", "10/90") — those would collapse "סיגמא 15/85" to just "סיגמא"
 * and make every Sigma fund look like a match.
 */
const SERIES_MONTH_YEAR = /(?:^|\s)(?:0?[1-9]|1[0-2])\/(?:2[0-9]|3[0-5])(?=\s|$)/g;

/**
 * Banks and Bizportal spell the same Israeli fund differently
 * ("סיגמא לונגשורט אג\"ח 07/26" vs "סיגמא לונג/שורט אג\"ח - קרן גידור בנאמנות").
 */
export function normalizeFundName(value?: string | null): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[״"]/g, "")
    .replace(/[׳']/g, "")
    .replace(SERIES_MONTH_YEAR, " ")
    .replace(/קרן גידור בנאמנות|קרן נאמנות/g, "")
    .replace(/long[\s/-]*short/gi, "לונגשורט")
    .replace(/לונג[\s/-]*שורט/g, "לונגשורט")
    .replace(/לונגשורט(?=מניות|אגח)/g, "לונגשורט ")
    .replace(/multi[\s-]*strategy/gi, "אסטרטג")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function fundNameSearchTokens(value?: string | null): string[] {
  return normalizeFundName(value)
    .split(" ")
    .filter((token) => token.length >= 3 && !/^\d+$/.test(token));
}

function namesMeaningfullyOverlap(left: string, right: string): boolean {
  if (!left || !right) return false;
  if (left === right) return true;
  const [shorter, longer] =
    left.length <= right.length ? [left, right] : [right, left];
  if (!longer.includes(shorter)) return false;
  // A leftover house name ("סיגמא") must not match every fund from that house.
  return shorter.split(" ").filter(Boolean).length >= 2;
}

export function findUniqueFundByName<T>(
  name: string | undefined,
  rows: T[],
  getName: (row: T) => string | undefined
): T | undefined {
  const needle = normalizeFundName(name);
  if (!needle || !rows.length) return undefined;
  const scored = rows
    .map((row) => ({ row, candidate: normalizeFundName(getName(row)) }))
    .filter(({ candidate }) => namesMeaningfullyOverlap(needle, candidate));
  const exact = scored.filter(({ candidate }) => candidate === needle);
  if (exact.length === 1) return exact[0].row;
  if (exact.length > 1) return undefined;
  return scored.length === 1 ? scored[0].row : undefined;
}

export function findFormattedStock(
  stocks: FrontendStockFormat[] | undefined,
  stockName: string | undefined,
  purchasePrice?: number
): FrontendStockFormat | undefined {
  if (!stocks?.length || !stockName) return undefined;
  const exact = stocks.find(
    (stock) => stock.value === stockName || stock.name === stockName
  );
  if (exact) return exact;
  const byFundName = findUniqueFundByName(
    stockName,
    stocks,
    (stock) => stock.name || stock.fullName || undefined
  );
  if (byFundName) return byFundName;
  if (!isTruncatedTaseNumber(stockName)) return undefined;
  const suffixMatches = stocks.filter(
    (stock) =>
      !!stock.value &&
      /^\d{6}$/.test(stock.value) &&
      stock.value.endsWith(stockName)
  );
  return pickByPurchasePrice(
    suffixMatches,
    (stock) => stock.worth,
    purchasePrice
  );
}
